import { Col, Container, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
	SectionHeadline,
	LoadingSpinner,
	ReactSwitch,
	ModalBoxSectionEditProfile,
	Button,
	FontAwesome,
	Collapsible,
	NoData,
	CommunityPartnerAreaCard
} from 'Elements';
import styles from 'partials/homepage/section/staff-section.module.scss';
import { useModal } from 'src/hooks/useModal';
import { Colors } from 'Types';
import { useEffect, useState } from 'react';
import { useCommunityPartnerArea } from 'src/hooks/useCommunityPartner';

type Props = {
	isLoadingProfile: boolean;
	isFetchingProfile: boolean;
	onChangeValue: (
		objKey: string,
		key: string,
		value: string | boolean
	) => void;
	partners: {
		enabled: boolean;
		headline: string;
		description: string;
	};
};

export const CommunityPartnerPartnerAreaSection = ({
	isLoadingProfile,
	onChangeValue,
	partners
}: Props) => {
	const { t } = useTranslation();
	const [openModal, toggleModal] = useModal();
	const perRow = 8;
	const [next, setNext] = useState(perRow);
	const [loading, setLoading] = useState(false);
	const { data, isLoading } = useCommunityPartnerArea({
		pageSize: 50,
		pageNumber: 1,
		keyword: ''
	});

	const handleMore = () => {
		setLoading(true);
		setNext(next + perRow);
	};

	useEffect(() => {
		setLoading(false);
	}, [next]);

	return (
		<section className="position-relative mb-5">
			<Container>
				{isLoadingProfile && (
					<div className="d-flex align-items-center justify-content-center">
						<LoadingSpinner />
					</div>
				)}
				{!isLoadingProfile && !isLoading && (
					<Collapsible
						header={
							<div
								className={`d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3 ${styles.switcherBox}`}>
								<div className="d-flex flex-wrap align-items-center gap-3">
									<h5 className="fs-5 fw-700 d-flex align-items-center">
										{' '}
										{t('menu.partners-area')}
									</h5>
									<ReactSwitch
										size="md"
										checked={partners?.enabled}
										onChange={(val: boolean) => {
											onChangeValue(
												'areas',
												'enabled',
												val
											);
										}}
									/>
									<span className="d-flex align-items-center mb-0 py-0 cursor-pointer">
										{t('forms.show-partners-area-in-home')}
									</span>
								</div>
								<div>
									<Button
										size="sm"
										color={Colors['white-gray']}
										onClick={() => toggleModal()}>
										<FontAwesome
											icon="circle-info"
											size="1x"
											className="text-gray me-1"
										/>
										{t('button.more')}
									</Button>
								</div>
							</div>
						}>
						{!loading && (
							<>
								<SectionHeadline
									title={partners?.headline}
									description={partners?.description}
									titlePlaceholder={`[${t(
										'placeholder.partners-area-title'
									)}]`}
									descriptionPlaceholder={`[${t(
										'placeholder.partners-area-description'
									)}]`}
									titleColor="text-gray-5"
									className="mx-auto text-center"
									onUpdateTitle={(val) =>
										onChangeValue('areas', 'headline', val)
									}
									onUpdateDescription={(val) =>
										onChangeValue(
											'areas',
											'description',
											val
										)
									}
								/>
								{data && data?.items?.length > 0 && (
									<Row className="justify-content-center">
										{data?.items
											?.slice(0, next)
											?.map((partner) => {
												return (
													<Col
														md={6}
														lg={3}
														key={partner.id}
														className="mb-4">
														<CommunityPartnerAreaCard
															key={partner.id}
															partner={partner}
														/>
													</Col>
												);
											})}
									</Row>
								)}
								{data?.items && data?.items?.length > next && (
									<div className="d-flex align-items-center justify-content-center mt-3 gap-3">
										<Button
											size="lg"
											color={Colors['border-gray-4']}
											label={
												t(
													'button.more-partners'
												) as string
											}
											className="rounded-pill"
											onClick={handleMore}
										/>
									</div>
								)}
								{data && data?.items?.length === 0 && (
									<NoData
										message={t('title.no-partner-yet')}
									/>
								)}
							</>
						)}
					</Collapsible>
				)}
			</Container>
			{!isLoadingProfile && !partners?.enabled && (
				<div className="overlay overlay_white overlay-box" />
			)}
			<ModalBoxSectionEditProfile
				isOpen={openModal}
				onClose={toggleModal}
				title="info.partners-title"
				description="info.partners-description"
			/>
		</section>
	);
};
